import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  LinearProgress,
  Link,
  Paper,
  Skeleton,
  Slider,
  Tab,
  Tabs,
  Typography,
} from '@mui/material'
import TuneIcon from '@mui/icons-material/Tune'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { PageHelpPopover } from '@/components/common/PageHelpPopover'
import { useAuthStore } from '@/stores/authStore'
import {
  DICTIONARY_FILL_SKILLS,
  dictionaryFillApi,
  type DictionaryFillApiError,
  type DictionaryFillItem,
  type DictionaryFillRun,
  type DictionaryFillSkillKey,
  type DictionaryFillVerdict,
} from '@/api/dictionaryFill'
import { useDictionaryFillRealtime } from '@/realtime/useDictionaryFillRealtime'
import { displayErrorMessage, getErrorMessage } from '@/utils/error'
import { ROUTES } from '@/utils/constants'
import '@/styles/dashboard.css'

/** Порядок кнопок вердиктов = hotkeys 1–5. */
const VERDICT_ORDER: DictionaryFillVerdict[] = ['known_well', 'known', 'unsure', 'unknown', 'skip']

const VERDICT_COLOR: Record<
  DictionaryFillVerdict,
  'success' | 'primary' | 'warning' | 'error' | 'default'
> = {
  known_well: 'success',
  known: 'primary',
  unsure: 'warning',
  unknown: 'error',
  skip: 'default',
}

/** Догружать следующий батч, когда неоценённых позиций меньше порога. */
const BATCH_REFILL_THRESHOLD = 10

function isTypingTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null
  if (!el) return false
  if (el.tagName === 'TEXTAREA' || el.isContentEditable) return true
  if (el.tagName !== 'INPUT') return false
  // Слайдеры/чекбоксы фокусируются после клика, но текст в них не печатают —
  // hotkeys 1–5 должны продолжать работать (глотать их — только текстовым полям).
  const type = (el as HTMLInputElement).type
  return !['range', 'checkbox', 'radio', 'button'].includes(type)
}

/**
 * Страница прогона «Пополнить словарь». Три режима на одной странице:
 * учитель ведёт активный прогон (вердикты hotkeys 1–5, пошкальная панель,
 * прозрачная подгрузка батчей, пересмотр кликом), ученик смотрит read-only
 * realtime (слово без перевода), завершённый прогон показывает сводку.
 */
export function DictionaryFillRunPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const params = useParams<{ runId: string }>()
  const runId = Number(params.runId)

  const authUserId = useAuthStore((s) => s.user?.id ?? null)

  const [run, setRun] = useState<DictionaryFillRun | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [hasMore, setHasMore] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [subscriptionError, setSubscriptionError] = useState(false)
  const [isAssessing, setIsAssessing] = useState(false)
  const [skillsOpen, setSkillsOpen] = useState(false)
  const [skillsDraft, setSkillsDraft] = useState<Partial<Record<DictionaryFillSkillKey, number>>>({})
  const [reviseItemId, setReviseItemId] = useState<number | null>(null)
  const [pendingVerdict, setPendingVerdict] = useState<DictionaryFillVerdict | null>(null)
  const [siblingRuns, setSiblingRuns] = useState<DictionaryFillRun[]>([])

  const isFetchingBatchRef = useRef(false)
  const runIdRef = useRef(runId)
  runIdRef.current = runId
  /** Растёт на каждой локальной мутации (assess): устаревший reload не должен затирать её. */
  const mutationSeqRef = useRef(0)

  const isTeacher = run !== null && authUserId !== null && run.teacher_id === authUserId
  const isActive = run?.status === 'active'
  const isTeacherRef = useRef(isTeacher)
  isTeacherRef.current = isTeacher

  // Переключение вкладок группового урока — полный сброс состояния прогона:
  // без setRun(null)/setIsLoading(true) эффект батча стрелял бы next-batch
  // нового runId по stale-состоянию предыдущего прогона.
  useEffect(() => {
    setRun(null)
    setIsLoading(true)
    setHasMore(true)
    setSkillsDraft({})
    setSkillsOpen(false)
    setReviseItemId(null)
    setError(null)
    setSubscriptionError(false)
  }, [runId])

  const reload = useCallback(async () => {
    if (!Number.isFinite(runId)) return null
    const requestedRunId = runId
    const mutationAtStart = mutationSeqRef.current
    try {
      const fresh = await dictionaryFillApi.get(requestedRunId)
      // Не применяем устаревший ответ: пользователь ушёл на другую вкладку
      // или успел оценить слово, пока запрос был в полёте.
      if (runIdRef.current !== requestedRunId || mutationSeqRef.current !== mutationAtStart) {
        return null
      }
      setRun(fresh)
      return fresh
    } catch (err) {
      if (runIdRef.current === requestedRunId) {
        setError(displayErrorMessage(getErrorMessage(err), t('common.error')))
      }
      return null
    }
  }, [runId, t])

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setIsLoading(true)
      const fresh = await reload()
      if (!cancelled && fresh) setIsLoading(false)
      if (!cancelled && !fresh) setIsLoading(false)
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [reload])

  const handleRealtimeUpdate = useCallback(
    (type: 'assessed' | 'batch' | 'completed' | 'poll') => {
      // Учителю его же событие оценки не нужно: состояние уже применено из
      // HTTP-ответа assess, а полный reload со старым снимком вернул бы
      // оценённое слово в очередь (бэкенд не шлёт toOthers()).
      if (type === 'assessed' && isTeacherRef.current) return
      void reload()
    },
    [reload],
  )

  useDictionaryFillRealtime(Number.isFinite(runId) ? runId : null, handleRealtimeUpdate)

  // Прогоны того же урока (групповой урок): вкладки по ученикам.
  const lessonId = run?.lesson_id ?? null
  useEffect(() => {
    if (lessonId === null) {
      setSiblingRuns([])
      return
    }
    let cancelled = false
    void dictionaryFillApi
      .list({ lesson_id: lessonId, per_page: 50 })
      .then((page) => {
        if (!cancelled) setSiblingRuns(page.data)
      })
      .catch(() => {
        if (!cancelled) setSiblingRuns([])
      })
    return () => {
      cancelled = true
    }
  }, [lessonId])

  const items = useMemo(() => run?.items ?? [], [run])
  const pendingItems = useMemo(() => items.filter((i) => i.assessed_at === null), [items])
  const assessedItems = useMemo(() => items.filter((i) => i.assessed_at !== null), [items])

  const currentItem: DictionaryFillItem | null = useMemo(() => {
    if (reviseItemId !== null) {
      return items.find((i) => i.id === reviseItemId) ?? null
    }
    return pendingItems[0] ?? null
  }, [items, pendingItems, reviseItemId])

  const fetchBatchIfNeeded = useCallback(
    async (force = false) => {
      if (!isTeacher || !isActive || !hasMore) return
      if (isFetchingBatchRef.current) return
      if (!force && pendingItems.length >= BATCH_REFILL_THRESHOLD) return
      isFetchingBatchRef.current = true
      try {
        const batch = await dictionaryFillApi.nextBatch(runId)
        setHasMore(batch.has_more)
        if (batch.items.length > 0) {
          await reload()
        }
      } catch (err) {
        const rich = err as DictionaryFillApiError
        if (rich.status !== 409) {
          if (rich.code === 'subscription_parameter_denied') setSubscriptionError(true)
          setError(displayErrorMessage(getErrorMessage(err), t('common.error')))
        }
        if (rich.status === 409) setHasMore(false)
      } finally {
        isFetchingBatchRef.current = false
      }
    },
    [isTeacher, isActive, hasMore, pendingItems.length, runId, reload, t],
  )

  useEffect(() => {
    if (!isLoading && isTeacher && isActive && pendingItems.length < BATCH_REFILL_THRESHOLD) {
      void fetchBatchIfNeeded(pendingItems.length === 0)
    }
  }, [isLoading, isTeacher, isActive, pendingItems.length, fetchBatchIfNeeded])

  const handleAssess = useCallback(
    async (verdict: DictionaryFillVerdict) => {
      if (!run || !currentItem || isAssessing) return
      setIsAssessing(true)
      setPendingVerdict(verdict)
      setError(null)
      try {
        const touched = Object.keys(skillsDraft).length > 0 ? skillsDraft : undefined
        const result = await dictionaryFillApi.assess(run.id, currentItem.id, {
          verdict,
          ...(verdict !== 'skip' && touched ? { skills: touched } : {}),
        })
        mutationSeqRef.current += 1
        setSubscriptionError(false)
        setSkillsDraft({})
        setSkillsOpen(false)
        setReviseItemId(null)
        setRun((prev) => {
          if (!prev) return prev
          return {
            ...prev,
            status: result.run_status,
            items_assessed: result.items_assessed,
            cards_created: result.cards_created,
            items: (prev.items ?? []).map((i) => (i.id === result.item.id ? result.item : i)),
          }
        })
        if (result.run_status !== 'active') {
          await reload()
        }
      } catch (err) {
        const rich = err as DictionaryFillApiError
        if (rich.code === 'subscription_parameter_denied') setSubscriptionError(true)
        setError(displayErrorMessage(getErrorMessage(err), t('common.error')))
        if (rich.status === 409 || rich.status === 422) {
          await reload()
        }
      } finally {
        setIsAssessing(false)
        setPendingVerdict(null)
      }
    },
    [run, currentItem, isAssessing, skillsDraft, reload, t],
  )

  // Hotkeys 1–5 — только у учителя на активном прогоне и вне полей ввода.
  useEffect(() => {
    if (!isTeacher || !isActive) return
    const handler = (e: KeyboardEvent) => {
      if (isTypingTarget(e.target)) return
      const index = ['1', '2', '3', '4', '5'].indexOf(e.key)
      if (index === -1) return
      e.preventDefault()
      void handleAssess(VERDICT_ORDER[index])
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [isTeacher, isActive, handleAssess])

  const [isClosing, setIsClosing] = useState(false)

  const handleComplete = async () => {
    if (!run || isClosing) return
    setIsClosing(true)
    try {
      mutationSeqRef.current += 1
      setRun(await dictionaryFillApi.complete(run.id))
      setError(null)
    } catch (err) {
      setError(displayErrorMessage(getErrorMessage(err), t('common.error')))
    } finally {
      setIsClosing(false)
    }
  }

  const handleCancel = async () => {
    if (!run || isClosing) return
    if (!window.confirm(t('dictionaryFill.run.cancelConfirm'))) return
    setIsClosing(true)
    try {
      mutationSeqRef.current += 1
      setRun(await dictionaryFillApi.cancel(run.id))
      setError(null)
    } catch (err) {
      setError(displayErrorMessage(getErrorMessage(err), t('common.error')))
    } finally {
      setIsClosing(false)
    }
  }

  const progressPct = useMemo(() => {
    if (!run || run.words_total_estimate === 0) return 0
    return Math.min(100, Math.round((run.items_assessed / run.words_total_estimate) * 100))
  }, [run])

  const verdictLabel = (verdict: DictionaryFillVerdict) => t(`dictionaryFill.verdict.${verdict}`)

  if (isLoading) {
    return (
      <DashboardLayout>
        <Box sx={{ p: 3 }}>
          <Skeleton variant="text" width={320} height={48} />
          <Skeleton variant="rounded" height={240} sx={{ mt: 2 }} />
          <Skeleton variant="rounded" height={120} sx={{ mt: 2 }} />
        </Box>
      </DashboardLayout>
    )
  }

  if (!run) {
    return (
      <DashboardLayout>
        <Box sx={{ p: 3 }}>
          <Alert severity="error">{error ?? t('dictionaryFill.run.notFound')}</Alert>
        </Box>
      </DashboardLayout>
    )
  }

  const summary = run.verdict_summary

  return (
    <DashboardLayout>
      <Box sx={{ p: 3 }}>
        <Breadcrumbs sx={{ mb: 1 }}>
          <Link component={RouterLink} to={ROUTES.DASHBOARD} underline="hover" color="inherit">
            {t('dashboard.home')}
          </Link>
          {run.lesson_id !== null && (
            <Link
              component={RouterLink}
              to={ROUTES.LESSON_DETAILS(run.lesson_id)}
              underline="hover"
              color="inherit"
            >
              {t('dictionaryFill.form.lessonCrumb', { id: run.lesson_id })}
            </Link>
          )}
          <Typography color="text.primary">
            {t('dictionaryFill.run.title', { id: run.id })}
          </Typography>
        </Breadcrumbs>

        {siblingRuns.length > 1 && (
          <Tabs
            value={siblingRuns.some((s) => s.id === run.id) ? run.id : false}
            onChange={(_e, value) => navigate(ROUTES.DICTIONARY_FILL_RUN(value as number))}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ mb: 1 }}
          >
            {siblingRuns.map((sibling) => (
              <Tab
                key={sibling.id}
                value={sibling.id}
                label={sibling.student?.name ?? `#${sibling.student_id}`}
              />
            ))}
          </Tabs>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography variant="h4" component="h1" fontWeight={700} sx={{ flexGrow: 1 }}>
            {t('dictionaryFill.run.heading', { student: run.student?.name ?? `#${run.student_id}` })}
          </Typography>
          <PageHelpPopover pageKey="dictionary-fill-run" />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <Chip
            label={run.status_label}
            color={run.status === 'active' ? 'success' : run.status === 'completed' ? 'default' : 'warning'}
            size="small"
          />
          <Typography variant="body2" color="text.secondary">
            {t('dictionaryFill.run.counters', {
              assessed: run.items_assessed,
              total: run.words_total_estimate,
              cards: run.cards_created,
            })}
          </Typography>
          {isTeacher && isActive && (
            <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
              <Button size="small" variant="outlined" disabled={isClosing} onClick={() => void handleComplete()}>
                {t('dictionaryFill.run.complete')}
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="warning"
                disabled={isClosing}
                onClick={() => void handleCancel()}
              >
                {t('dictionaryFill.run.cancel')}
              </Button>
            </Box>
          )}
        </Box>

        <LinearProgress
          variant="determinate"
          value={progressPct}
          sx={{ height: 8, borderRadius: 99, mb: 2 }}
        />

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
            {subscriptionError && (
              <Box sx={{ mt: 1 }}>
                <Button
                  component={RouterLink}
                  to={ROUTES.PROFILE_SUBSCRIPTION}
                  variant="outlined"
                  size="small"
                >
                  {t('subscription.goToSubscription')}
                </Button>
              </Box>
            )}
          </Alert>
        )}

        {isActive && currentItem && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              {reviseItemId !== null && (
                <Alert severity="info" sx={{ mb: 2 }} onClose={() => setReviseItemId(null)}>
                  {t('dictionaryFill.run.revising')}
                </Alert>
              )}
              <Typography variant="h3" component="div" align="center" sx={{ my: 2 }}>
                {currentItem.word?.text ?? '—'}
              </Typography>
              {isTeacher && currentItem.word?.transcription && (
                <Typography variant="body1" color="text.secondary" align="center">
                  [{currentItem.word.transcription}]
                </Typography>
              )}
              {isTeacher && currentItem.word?.translation && (
                <Typography variant="h6" color="text.secondary" align="center" sx={{ mb: 2 }}>
                  {currentItem.word.translation}
                </Typography>
              )}

              {isTeacher ? (
                <>
                  <Box
                    sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap', mb: 1 }}
                  >
                    {VERDICT_ORDER.map((verdict, index) => (
                      <Button
                        key={verdict}
                        variant="outlined"
                        color={VERDICT_COLOR[verdict] === 'default' ? 'inherit' : VERDICT_COLOR[verdict]}
                        disabled={isAssessing}
                        onClick={() => void handleAssess(verdict)}
                        startIcon={
                          pendingVerdict === verdict ? <CircularProgress size={16} /> : undefined
                        }
                      >
                        {verdictLabel(verdict)}
                        <Typography component="span" variant="caption" sx={{ ml: 0.75, opacity: 0.6 }}>
                          {index + 1}
                        </Typography>
                      </Button>
                    ))}
                  </Box>
                  <Typography variant="caption" color="text.secondary" align="center" display="block">
                    {t('dictionaryFill.run.hotkeysHint')}
                  </Typography>

                  <Box sx={{ mt: 2 }}>
                    <Button
                      size="small"
                      startIcon={<TuneIcon fontSize="small" />}
                      variant={skillsOpen ? 'contained' : 'outlined'}
                      onClick={() => setSkillsOpen((v) => !v)}
                    >
                      {t('dictionaryFill.run.skillsPanel')}
                    </Button>
                    <Collapse in={skillsOpen}>
                      <Paper variant="outlined" sx={{ p: 2, mt: 1 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {t('dictionaryFill.run.skillsHint')}
                        </Typography>
                        {DICTIONARY_FILL_SKILLS.map((skill) => (
                          <Box
                            key={skill}
                            sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5 }}
                          >
                            <Typography variant="body2" sx={{ width: 180, flexShrink: 0 }}>
                              {t(`dictionaryFill.skills.${skill}`)}
                            </Typography>
                            <Slider
                              size="small"
                              min={0}
                              max={100}
                              value={skillsDraft[skill] ?? 0}
                              onChange={(_e, value) =>
                                setSkillsDraft((prev) => ({ ...prev, [skill]: value as number }))
                              }
                              valueLabelDisplay="auto"
                            />
                            <Typography
                              variant="body2"
                              sx={{ width: 36, textAlign: 'right', fontFamily: 'monospace' }}
                            >
                              {skillsDraft[skill] ?? '—'}
                            </Typography>
                          </Box>
                        ))}
                        {Object.keys(skillsDraft).length > 0 && (
                          <Button size="small" onClick={() => setSkillsDraft({})}>
                            {t('dictionaryFill.run.skillsReset')}
                          </Button>
                        )}
                      </Paper>
                    </Collapse>
                  </Box>
                </>
              ) : (
                <Typography variant="body2" color="text.secondary" align="center">
                  {t('dictionaryFill.run.studentWaiting')}
                </Typography>
              )}
            </CardContent>
          </Card>
        )}

        {isActive && !currentItem && isTeacher && (
          <Alert severity="info" sx={{ mb: 3 }}>
            {hasMore ? t('dictionaryFill.run.loadingBatch') : t('dictionaryFill.run.allAssessed')}
          </Alert>
        )}

        {!isActive && summary && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('dictionaryFill.run.summaryTitle')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                {VERDICT_ORDER.map((verdict) => (
                  <Chip
                    key={verdict}
                    label={`${verdictLabel(verdict)}: ${summary[verdict]}`}
                    color={VERDICT_COLOR[verdict] === 'default' ? undefined : VERDICT_COLOR[verdict]}
                    variant="outlined"
                  />
                ))}
              </Box>
              {isTeacher && (
                <Button
                  component={RouterLink}
                  to={ROUTES.USER_CARDS(run.student_id)}
                  size="small"
                  variant="outlined"
                >
                  {t('dictionaryFill.run.openStudentCards')}
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {assessedItems.length > 0 && (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('dictionaryFill.run.assessedTitle', { count: assessedItems.length })}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {assessedItems.map((item) => (
                  <Box
                    key={item.id}
                    onClick={
                      isTeacher && isActive
                        ? () => {
                            setReviseItemId(item.id)
                            // Пересмотр стартует с прежних пошкальных значений учителя.
                            setSkillsDraft(item.skills_override ?? {})
                            if (item.skills_override) setSkillsOpen(true)
                          }
                        : undefined
                    }
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      py: 0.5,
                      px: 1,
                      borderRadius: 1,
                      cursor: isTeacher && isActive ? 'pointer' : 'default',
                      '&:hover':
                        isTeacher && isActive ? { backgroundColor: 'action.hover' } : undefined,
                    }}
                  >
                    <Typography variant="body2" sx={{ flexGrow: 1 }}>
                      {item.word?.text ?? '—'}
                    </Typography>
                    {item.revised_count > 0 && (
                      <Typography variant="caption" color="text.secondary">
                        {t('dictionaryFill.run.revised')}
                      </Typography>
                    )}
                    {item.verdict && (
                      <Chip
                        size="small"
                        label={verdictLabel(item.verdict)}
                        color={
                          VERDICT_COLOR[item.verdict] === 'default'
                            ? undefined
                            : VERDICT_COLOR[item.verdict]
                        }
                        variant="outlined"
                      />
                    )}
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        )}
      </Box>
    </DashboardLayout>
  )
}
